import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFormik, FieldArray, Field, ErrorMessage, FormikProvider, Form } from 'formik';
import * as Yup from 'yup';
import './sales.css';
import appclient from '../../../AppClient';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const INITIAL_SALE_ITEM = {
  product: '',
  quantity: 1,
  sale_price: '',
  credit_sale_price: '',
  discount: 0,
  total_price: 0.00
};

const SalesPage = () => {
  const {paymentMethods } = useAuth();
  const navigate = useNavigate();
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [productInventory, setProductInventory] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const salesPerPage = 10;
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [itemSearchTerms, setItemSearchTerms] = useState({});
  const productSelectRef = useRef(null);

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );
  // Improved API calls with proper error handling
  const fetchData = useCallback(async (endpoint, stateSetter, errorMessage) => {
    try {
      setIsLoading(true);
      const response = await appclient.get(endpoint);
      stateSetter(response.data);
    } catch (err) {
      console.error(`${errorMessage}:`, err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(() => {
    fetchData('/customers/', setCustomers, 'فشل في تحميل العملاء');
  }, [fetchData]);

  const fetchProducts = useCallback(() => {
    fetchData('/products/', (data) => {
      setProducts(data);
      const inventoryMap = {};
      data.forEach(product => {
        inventoryMap[product.id] = {
          stock: product.quantity_available || 0,
          sale_price: product.sale_price || 0,
          credit_sale_price: product.credit_sale_price || 0
        };
      });
      setProductInventory(inventoryMap);
    }, 'فشل في تحميل المنتجات');
  }, [fetchData]);

  const fetchStaff = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await appclient.get('/staff/');
      setStaff(response.data.results || response.data.staff || response.data || []);
    } catch (err) {
      console.error('فشل في تحميل الموظفين:', err);
      setError(err.response?.data?.detail || 'فشل في تحميل بيانات الموظفين');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSales = useCallback(() => {
    fetchData('/transactions/sales/', (data) => {
      setAllSales(data);
      setFilteredSales(data);
    }, 'فشل في تحميل المبيعات');
  }, [fetchData]);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchStaff();
    fetchSales();
  }, [fetchCustomers, fetchProducts, fetchStaff, fetchSales]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSales(allSales);
      setCurrentPage(1);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = allSales.filter(sale => {
      return (
        (sale.id?.toString() || '').toLowerCase().includes(searchTermLower) ||
        (sale.customer?.name || '').toLowerCase().includes(searchTermLower) ||
        (sale.payment_method || '').toLowerCase().includes(searchTermLower) ||
        (sale.total_price?.toString() || '').toLowerCase().includes(searchTermLower)
      );
    });
    setFilteredSales(filtered);
    setCurrentPage(1);
  }, [searchTerm, allSales]);

  const calculateLineItemTotal = useCallback((quantity, price, discount) => {
    const qty = parseFloat(quantity) || 0;
    const prc = parseFloat(price) || 0;
    const disc = parseFloat(discount) || 0;
    
    const totalBeforeDiscount = qty * prc;
    const totalAfterDiscount = totalBeforeDiscount - (totalBeforeDiscount * (disc / 100));
    return parseFloat(totalAfterDiscount).toFixed(2);
  }, []);

  const calculateTotalPrice = useCallback((items, discount = 0) => {
    if (!items || !items.length) return '0.00';
    
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.total_price) || 0);
    }, 0);
    
    const discountRate = parseFloat(discount) / 100;
    const discountedTotal = total * (1 - (isNaN(discountRate) ? 0 : discountRate));
    
    return discountedTotal > 0 ? discountedTotal.toFixed(2) : '0.00';
  }, []);

  const handleDeleteSale = useCallback(async (saleId) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في الغاء عملية البيع هذه؟')) {
      return;
    }
  
    try {
      setIsLoading(true);
      await appclient.delete(`/transactions/sales/${saleId}/cancel/`);
      await fetchSales();
      setError(null);
    } catch (err) {
      setError('فشل في الغاء عملية البيع');
      console.error('خطأ في الغاء عملية البيع:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchSales]);

  const checkProductStock = useCallback((productId, requestedQuantity) => {
    if (!productId || !productInventory[productId]) return true;
    const availableStock = productInventory[productId].stock;
    return availableStock >= requestedQuantity;
  }, [productInventory]);

  const saleItemSchema = Yup.object().shape({
    product: Yup.string().required('المنتج مطلوب'),
    quantity: Yup.number().required('الكمية مطلوبة').min(1, 'يجب أن تكون الكمية على الأقل 1'),
    sale_price: Yup.number().required('سعر البيع مطلوب').min(0, 'يجب أن يكون سعر البيع قيمة موجبة'),
    credit_sale_price: Yup.number().min(0, 'يجب أن يكون سعر البيع قيمة موجبة'),
    discount: Yup.number().min(0, 'يجب أن يكون الخصم قيمة موجبة').max(100, 'يجب أن يكون الخصم أقل من أو يساوي 100'),
    total_price: Yup.string()
  });
  
  const validationSchema = Yup.object({
    payment_method: Yup.string().required('طريقة الدفع مطلوبة'),
    customer: Yup.number().required('العميل مطلوب'),
    discount_value: Yup.number()
      .min(0, 'يجب أن يكون الخصم قيمة موجبة')
      .max(100, 'يجب أن يكون الخصم أقل من أو يساوي 100%')
      .transform((value) => (isNaN(value) ? 0 : value)),
    discount_user_name: Yup.string().when('discount_value', {
      is: (val) => val > 0,
      then: () => Yup.string().required('اسم الموظف المسؤول عن الخصم مطلوب عند تطبيق الخصم'),
      otherwise: () => Yup.string()
    }),
    number_of_installments: Yup.number().when('payment_method', {
      is: 'credit',
      then: () => Yup.number().min(1, 'يجب أن يكون عدد الأقساط على الأقل 1'),
      otherwise: () => Yup.number().min(1)
    }),
    sale_items: Yup.array().of(saleItemSchema).min(1, 'الرجاء إضافة منتج واحد على الأقل')
  });

  const formik = useFormik({
    initialValues: {
      payment_method: 'نقدي',
      number_of_installments: 1,
      customer: '',
      discount_value: 0.00,
      discount_user_name: '',
      total_price: calculateTotalPrice([{ ...INITIAL_SALE_ITEM }], 0),
      sale_items: [{ ...INITIAL_SALE_ITEM }],
    },
    validationSchema,
    onSubmit: async (values) => {
      if (values.sale_items.length === 0 || values.sale_items.some(item => !item.product)) {
        setError('الرجاء إضافة منتج واحد على الأقل مع جميع الحقول المطلوبة');
        return;
      }

      for (const item of values.sale_items) {
        if (!checkProductStock(item.product, item.quantity)) {
          setError(`المنتج غير متوفر بالكمية المطلوبة (${item.quantity})`);
          return;
        }
      }

      try {
        setIsLoading(true);
        setError(null);
        const saleData = {
          ...values,
          total_price: parseFloat(calculateTotalPrice(values.sale_items, values.discount_value)),
          sale_items: values.sale_items.map(item => ({
            product: parseInt(item.product, 10),
            quantity: parseInt(item.quantity, 10),
            sale_price: parseFloat(item.sale_price),
            credit_sale_price: parseFloat(item.credit_sale_price),
            discount: item.discount,
            total_price: parseFloat(item.total_price)
          }))
        };
        
        await appclient.post('/transactions/sales/add/', saleData);
        formik.resetForm();
        setTotalPrice(0);
        await fetchSales();
        await fetchProducts();
      } catch (err) {
        setError(err.response?.data?.detail || 'فشل في تسجيل عملية البيع');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const updateItemField = useCallback((itemIndex, fieldName, fieldValue) => {
    const allItems = [...formik.values.sale_items];
    allItems[itemIndex][fieldName] = fieldValue;

    // Recalculate total for this item
    allItems[itemIndex].total_price = calculateLineItemTotal(
      allItems[itemIndex].quantity,
      allItems[itemIndex].sale_price,
      allItems[itemIndex].discount
    );

    // Update Formik values
    formik.setValues({
      ...formik.values,
      sale_items: allItems,
      total_price: parseFloat(calculateTotalPrice(allItems, formik.values.discount_value)),
    });
  }, [formik.values, calculateLineItemTotal, calculateTotalPrice]);

  const handleProductSelect = useCallback((itemIndex, productId) => {
    if (!productId) return;
    const selectedProduct = products.find(p => p.id === parseInt(productId, 10));
    if (!selectedProduct) return;
    
    const price = formik.values.payment_method === 'credit' 
      ? (selectedProduct.credit_sale_price || selectedProduct.sale_price || '0')
      : (selectedProduct.sale_price || '0');
    
    updateItemField(itemIndex, 'product', productId);
    updateItemField(itemIndex, 'sale_price', price);
    updateItemField(itemIndex, 'credit_sale_price', selectedProduct.credit_sale_price || price);
  }, [products, formik.values.payment_method, updateItemField]);

  useEffect(() => {
    if (formik.values.sale_items.length > 0 && products.length > 0) {
      const updatedItems = formik.values.sale_items.map(item => {
        if (item.product) {
          const selectedProduct = products.find(p => p.id === parseInt(item.product, 10));
          if (selectedProduct) {
            const newPrice = formik.values.payment_method === 'credit' 
              ? (selectedProduct.credit_sale_price || selectedProduct.sale_price || '0')
              : (selectedProduct.sale_price || '0');
            
            if (item.sale_price !== newPrice) {
              return {
                ...item,
                sale_price: newPrice,
                credit_sale_price: selectedProduct.credit_sale_price || newPrice,
                total_price: calculateLineItemTotal(item.quantity, newPrice, item.discount)
              };
            }
          }
        }
        return item;
      });
      
      formik.setValues({
        ...formik.values,
        sale_items: updatedItems,
        total_price: calculateTotalPrice(updatedItems, formik.values.discount_value)
      });
    }
  }, [formik.values.payment_method, products]);

  // Pagination logic
  const indexOfLastSale = currentPage * salesPerPage;
  const indexOfFirstSale = indexOfLastSale - salesPerPage;
  const currentSales = filteredSales.slice(indexOfFirstSale, indexOfLastSale);
  const totalPages = Math.ceil(filteredSales.length / salesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-EG');
    } catch (e) {
      return 'تاريخ غير صالح';
    }
  };

  const RecentSalesTable = () => (
    <>
      <table className="sales-table">
        <thead>
          <tr>
            <th>الرقم</th>
            <th>العميل</th>
            <th>طريقة الدفع</th>
            <th>العناصر</th>
            <th>الإجمالي</th>
            <th>التاريخ</th>
            <th>الإجراءات</th>
          </tr>
        </thead>
        <tbody>
          {currentSales.map(sale => (
            <tr key={sale.id}>
              <td>{sale.id}</td>
              <td>{sale.customer || 'N/A'}</td>
              <td>{sale.payment_method}</td>
              <td>{sale.total_quantity || 0}</td>
              <td>{sale.total_price}</td>
              <td>{formatDate(sale.invoice_date)}</td>
              <td>
                <button 
                  type="button"
                  className="btn"
                  onClick={() => navigate(`/transactions/sales/${sale.id}`)}
                  disabled={isLoading}
                >
                  <i className="fas fa-eye"></i> 
                </button>
                {sale.status === 'completed' ? (
                <button 
                  type="button"
                  className="btn btn-danger delete-btn"
                  onClick={() => handleDeleteSale(sale.id)}
                  disabled={isLoading}
                >
                  <i className="fas fa-trash mx-1"></i>
                  <span>إلغاء العملية</span>
                </button>
                ) : (
                  <span className="bg-warning text-white px-2 py-1 rounded">ملغاة</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            type="button"
            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1 || isLoading}
          >
            السابق
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
            <button
              type="button"
              key={number}
              onClick={() => paginate(number)}
              className={currentPage === number ? 'active' : ''}
              disabled={isLoading}
            >
              {number}
            </button>
          ))}
          
          <button 
            type="button"
            onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
            disabled={currentPage === totalPages || isLoading}
          >
            التالي
          </button>
        </div>
      )}
    </>
  );

  const getFilteredProducts = useCallback((index) => {
    const searchTerm = itemSearchTerms[index] || '';
    const availableProducts = products.filter(product => product.quantity_available > 0);
    if (!searchTerm.trim()) return availableProducts;
    
    return availableProducts.filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.id.toString().includes(searchTerm)
    );
  }, [products, itemSearchTerms]);

  const handleProductSearch = useCallback((index, searchTerm) => {
    setItemSearchTerms(prev => ({
      ...prev,
      [index]: searchTerm
    }));
  }, []);

  const clearProductSearch = useCallback((index) => {
    setItemSearchTerms(prev => ({
      ...prev,
      [index]: ''
    }));
    setActiveItemIndex(null);
  }, []);

  return (
    <div className="sales-page" dir="rtl" lang="ar">
      <h1>إدارة المبيعات</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="sales-form-container">
        <h2>إضافة عملية بيع جديدة</h2>
        <FormikProvider value={formik}>
          <Form>
            <div className="form-group">
              <label htmlFor="customer">العميل</label>
              <Field
                as="select"
                id="customer"
                name="customer"
                className={formik.touched.customer && formik.errors.customer ? 'error' : ''}
              >
                <option value="">اختر العميل</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </Field>
              <ErrorMessage name="customer" component="div" className="error-text" />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payment_method">طريقة الدفع</label>
                <Field
                  as="select"
                  id="payment_method"
                  name="payment_method"
                  className={formik.touched.payment_method && formik.errors.payment_method ? 'error' : ''}
                >
                  <option value="">اختر طريقة الدفع</option>
                  {paymentMethods.filter(method => method.is_sale==true).map(method => (
                    <option key={method.name} value={method.name}>{method.arabic_name}</option>
                  ))}
                </Field>
                <ErrorMessage name="payment_method" component="div" className="error-text" />
              </div>
              
              <div className="form-group">
                <label htmlFor="number_of_installments">عدد الأقساط</label>
                <Field
                  type="number"
                  id="number_of_installments"
                  name="number_of_installments"
                  min="1"
                  disabled={formik.values.payment_method !== 'credit'}
                />
                <ErrorMessage name="number_of_installments" component="div" className="error-text" />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="discount_value">قيمة الخصم (%)</label>
                <Field
                  type="number"
                  step="0.01"
                  id="discount_value"
                  name="discount_value"
                  min="0"
                  max="100"
                />
                <ErrorMessage name="discount_value" component="div" className="error-text" />
              </div>
              
              <div className="form-group">
                <label htmlFor="discount_user_name">موظف الخصم</label>
                <Field
                  as="select"
                  id="discount_user_name"
                  name="discount_user_name"
                  disabled={parseFloat(formik.values.discount_value) <= 0}
                  className={formik.touched.discount_user_name && formik.errors.discount_user_name ? 'error' : ''}
                >
                  <option value="">اختر الموظف</option>
                  {staff.map(employee => (
                    <option key={employee.id} value={employee.name}>
                      {employee.name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="discount_user_name" component="div" className="error-text" />
              </div>
            </div>
            
            <div className="sale-items">
              <h3>عناصر البيع</h3>
              <FieldArray name="sale_items">
                {({ remove, push }) => (
                  <>
                    {formik.values.sale_items.map((item, index) => (
                      <div key={index} className="sale-item">
                        <div className="item-header">
                          <h4>العنصر #{index + 1}</h4>
                          {formik.values.sale_items.length > 1 && (
                            <button 
                              type="button" 
                              className="remove-item-btn"
                              onClick={() => {
                                const newItems = formik.values.sale_items.filter((_, i) => i !== index);
                                formik.setValues({
                                  ...formik.values,
                                  sale_items: newItems,
                                  total_price: calculateTotalPrice(newItems, formik.values.discount_value)
                                });
                              }}
                            >
                              إزالة
                            </button>
                          )}
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>المنتج</label>
                            <div className="product-select-container">
                              <input
                                type="text"
                                placeholder="ابحث عن منتج بالاسم أو الرقم..."
                                value={itemSearchTerms[index] || ''}
                                onChange={(e) => handleProductSearch(index, e.target.value)}
                                onClick={() => setActiveItemIndex(index)}
                                className="product-select-search"
                              />
                              
                              <Field name={`sale_items[${index}].product`}>
                                {({ field, form }) => (
                                  <select
                                    {...field}
                                    ref={activeItemIndex === index ? productSelectRef : null}
                                    className={`product-select ${
                                      form.touched.sale_items?.[index]?.product && 
                                      form.errors.sale_items?.[index]?.product ? 'error' : ''
                                    }`}
                                    onChange={(e) => {
                                      form.handleChange(e);
                                      handleProductSelect(index, e.target.value);
                                      clearProductSearch(index);
                                    }}
                                    size={5} // Show multiple options
                                  >
                                    <option value="" 
                                        style={{

                                          color: '#999',
                                          padding: '8px',
                                          marginBottom: '4px',
                                          borderBottom: '1px solid #eee'
                                        }}
                                      >
                                      اختر منتجاً
                                      </option>
                                    {getFilteredProducts(index).map(product => (
                                      <option 
                                        key={product.id} 
                                        value={product.id} 
                                        disabled={product.quantity_available <= 0}
                                        style={{
                                          backgroundColor: product.quantity_available <= 0 ? '#f8f8f8' : 
                                                        product.quantity_available < 5 ? '#fff9c4' : 'white',
                                          color: product.quantity_available <= 0 ? '#999' : 'black',
                                          padding: '8px',
                                          marginBottom: '4px',
                                          borderBottom: '1px solid #eee'
                                        }}
                                      >
                                        {product.name} - المخزون: {product.quantity_available || 0} - نقدي: {product.sale_price} - ذمم: {product.credit_sale_price || product.sale_price}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </Field>
                              <ErrorMessage 
                                name={`sale_items[${index}].product`} 
                                component="div" 
                                className="error-text" 
                              />
                            </div>
                          </div>
                          
                          <div className="form-group">
                            <label>الكمية</label>
                            <Field
                              type="number"
                              name={`sale_items[${index}].quantity`}
                              min="1"
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 1;
                                updateItemField(index, 'quantity', newValue);
                              }}
                              className={
                                formik.touched.sale_items?.[index]?.quantity && 
                                formik.errors.sale_items?.[index]?.quantity ? 'error' : ''
                              }
                            />
                            <ErrorMessage 
                              name={`sale_items[${index}].quantity`} 
                              component="div" 
                              className="error-text" 
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>سعر البيع</label>
                            <Field
                              type="number"
                              step="0.01"
                              name={`sale_items[${index}].sale_price`}
                              readOnly
                              className={
                                formik.touched.sale_items?.[index]?.sale_price && 
                                formik.errors.sale_items?.[index]?.sale_price ? 'error' : ''
                              }
                            />
                            <ErrorMessage 
                              name={`sale_items[${index}].sale_price`} 
                              component="div" 
                              className="error-text" 
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>سعر البيع بالائتمان</label>
                            <Field
                              type="number"
                              step="0.01"
                              name={`sale_items[${index}].credit_sale_price`}
                              readOnly
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>خصم (%)</label>
                            <Field
                              type="number"
                              step="1"
                              name={`sale_items[${index}].discount`}
                              min="0"
                              max="100"
                              onChange={(e) => {
                                const newValue = parseFloat(e.target.value) || 0;
                                updateItemField(index, 'discount', newValue);
                              }}
                              className={
                                formik.touched.sale_items?.[index]?.discount && 
                                formik.errors.sale_items?.[index]?.discount ? 'error' : ''
                              }
                            />
                            <ErrorMessage 
                              name={`sale_items[${index}].discount`} 
                              component="div" 
                              className="error-text" 
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>إجمالي الصنف</label>
                            <Field
                              type="text"
                              name={`sale_items[${index}].total_price`}
                              value={formik.values.sale_items[index].total_price || '0.00'}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      className="add-item-btn"
                      onClick={() => push({ ...INITIAL_SALE_ITEM })}
                      disabled={isLoading}
                    >
                      إضافة عنصر آخر
                    </button>
                  </>
                )}
              </FieldArray>
            </div>
            
            <div className="total-section">
              <h3>الإجمالي بعد الخصم: {formik.values.total_price}</h3>
            </div>
            
            <div className="form-actions text-center">
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isLoading || !formik.isValid}
              >
                {isLoading ? 'جاري الإرسال...' : 'تسجيل عملية البيع'}
              </button>
            </div>
          </Form>
        </FormikProvider>
      </div>
      
      <div className="sales-list">
        <div className="sales-header">
          <h2>عمليات البيع الأخيرة</h2>
          <div className="search-bar">
            <input
              type="text"
              placeholder="ابحث عن مبيعات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
            />
            <i className="fas fa-search"></i>
          </div>
        </div>
        
        {isLoading ? (
          <p>جاري تحميل المبيعات...</p>
        ) : filteredSales.length === 0 ? (
          <p>لا توجد عمليات بيع</p>
        ) : (
          <RecentSalesTable />
        )}
      </div>
    </div>
  );
};

export default SalesPage;